import type { IntentTemplate } from "../types";
import { formatCountSummary, getArrayLength } from "./shared";

export const SERVICE_INTENT_TEMPLATES = {
  MANAGE_SERVICE: {
    label: "Service",
    pendingTitle: "Review Service Change",
    summary: (payload) =>
      typeof payload.serviceId === "string"
        ? "Existing service will be updated."
        : "A new service will be created.",
    fieldLabels: {
      serviceId: "Service ID",
      name: "Name",
      serviceCode: "Service Code",
      categoryId: "Category ID",
      basePrice: "Base Price",
      duration: "Duration (min)",
      description: "Description",
      allowOnlineBooking: "Online Booking",
      isActive: "Active",
    },
  },
  MANAGE_CATEGORY: {
    label: "Category",
    pendingTitle: "Review Category Change",
    summary: (payload) =>
      typeof payload.categoryId === "string"
        ? "Existing category will be updated."
        : "A new category will be created.",
    fieldLabels: {
      categoryId: "Category ID",
      name: "Name",
      description: "Description",
      color: "Color",
      icon: "Icon",
      allowOnlineBooking: "Online Booking",
      isArchived: "Archived",
    },
  },
  SORT_ITEMS: {
    label: "Sorting",
    pendingTitle: "Review Sort Changes",
    summary: (payload) => {
      const kind = typeof payload.type === "string" ? payload.type : "items";
      const count = getArrayLength(payload.items);
      if (count === undefined) {
        return `Order for ${kind} will be updated.`;
      }
      return `Order for ${count} ${kind} will be updated.`;
    },
    fieldLabels: {
      type: "Target Type",
      items: "Sort Items",
    },
  },
  BATCH_UPDATE_SERVICES: {
    label: "Batch Update",
    pendingTitle: "Review Batch Service Updates",
    summary: (payload) =>
      formatCountSummary(getArrayLength(payload.updates), "service", "services"),
    fieldLabels: {
      updates: "Service Updates",
    },
  },
  BULK_UPDATE_SERVICES: {
    label: "Bulk Update",
    pendingTitle: "Review Bulk Service Updates",
    summary: (payload) => {
      const count = getArrayLength(payload.serviceIds);
      if (count !== undefined) {
        return `${count} services will receive bulk updates.`;
      }

      if (typeof payload.categoryId === "string") {
        return "All services in one category will receive bulk updates.";
      }

      return "Bulk updates to services are proposed.";
    },
    fieldLabels: {
      serviceIds: "Service IDs",
      categoryId: "Category ID",
      priceAdjustment: "Price Adjustment",
      durationAdjustment: "Duration Adjustment",
      categoryChange: "Move To Category",
      archive: "Archive",
      allowOnlineBooking: "Online Booking",
    },
  },
  COPY_SERVICES: {
    label: "Copy Services",
    pendingTitle: "Review Service Copy Plan",
    summary: (payload) => {
      const count = getArrayLength(payload.serviceIds);
      if (count === undefined) {
        return "Services will be copied to another category.";
      }
      return `${count} services will be copied to another category.`;
    },
    fieldLabels: {
      serviceIds: "Service IDs",
      targetCategoryId: "Target Category ID",
      priceAdjustment: "Price Adjustment",
      namePrefix: "Name Prefix",
      nameSuffix: "Name Suffix",
    },
  },
  UPDATE_WORKER_SERVICES: {
    label: "Worker Restrictions",
    pendingTitle: "Review Worker Service Restrictions",
    summary: (payload) => {
      const mode =
        typeof payload.restrictionMode === "string" ? payload.restrictionMode : "UNKNOWN";
      return `Worker access mode will be set to ${mode}.`;
    },
    fieldLabels: {
      workerId: "Worker ID",
      restrictionMode: "Restriction Mode",
      serviceIds: "Service IDs",
      categoryIds: "Category IDs",
    },
  },
  BATCH_CREATE_SERVICES: {
    label: "Batch Create Services",
    pendingTitle: "Review New Services",
    summary: (payload) =>
      formatCountSummary(getArrayLength(payload.services), "service", "services"),
    fieldLabels: {
      services: "Services",
    },
  },
  BATCH_CREATE_CATEGORIES: {
    label: "Batch Create Categories",
    pendingTitle: "Review New Categories",
    summary: (payload) =>
      formatCountSummary(getArrayLength(payload.categories), "category", "categories"),
    fieldLabels: {
      categories: "Categories",
    },
  },
  BATCH_UPDATE_CATEGORIES: {
    label: "Batch Update Categories",
    pendingTitle: "Review Batch Category Updates",
    summary: (payload) =>
      formatCountSummary(getArrayLength(payload.updates), "category", "categories"),
    fieldLabels: {
      updates: "Category Updates",
      categoryId: "Category",
      name: "Name",
      color: "Color",
    },
  },
} satisfies Record<string, IntentTemplate>;
